import type { StaticWhitespaceCardProps } from 'components/organism/WhitespaceAnalysis/StaticWhitespaceCard';
import type { WhitespaceGridCell as WhitespaceGridDataCell } from 'hooks/useWhitespaceAnalysisGridData';
import { useCallback, useRef } from 'react';
import StaticWhitespaceCard from 'components/organism/WhitespaceAnalysis/StaticWhitespaceCard';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_WHITESPACE_CELL_HOVER,
    PPA_WHITESPACE_ENTITY_LIST_COLLAPSED,
    PPA_WHITESPACE_ENTITY_MORE_CLICKED,
} from 'utils/mixpanelEventNames';
import { buildWhitespaceCellId, getWhitespaceCellHoverProps } from './whitespaceMixpanel.helpers';

type WhitespaceGridCell = Partial<StaticWhitespaceCardProps>;

type UseWhitespaceCellRendererParams = {
    yAxis: string;
    crossSection: string;
    showWhitespaces: boolean;
    selectedYAxisKpiId: string;
    getCellPaginationData: (params: {
        bucketLabel: string;
        columnHeader: string;
        defaultItems: StaticWhitespaceCardProps['items'];
        defaultMoreCount: number;
    }) => {
        items: StaticWhitespaceCardProps['items'];
        moreCount: number;
        isLoading: boolean;
        showCollapse: boolean;
    };
    collapseCellItems: (params: { bucketLabel: string; columnHeader: string }) => void;
    loadMoreForCell: (params: {
        bucketLabel: string;
        columnHeader: string;
        defaultItems: StaticWhitespaceCardProps['items'];
        defaultMoreCount: number;
    }) => void;
};

const useWhitespaceCellRenderer = ({
    yAxis,
    crossSection,
    showWhitespaces,
    selectedYAxisKpiId,
    getCellPaginationData,
    collapseCellItems,
    loadMoreForCell,
}: UseWhitespaceCellRendererParams) => {
    const hoveredCellsRef = useRef<Set<string>>(new Set());

    const renderWhitespaceCell = useCallback(
        (cell: WhitespaceGridCell, rowLabel: string, columnHeader: string) => {
            const paginationData = getCellPaginationData({
                bucketLabel: rowLabel,
                columnHeader,
                defaultItems: cell.items ?? [],
                defaultMoreCount: cell.moreCount ?? 0,
            });
            const cellId = buildWhitespaceCellId(columnHeader, yAxis, rowLabel);
            const gridCell = cell as WhitespaceGridDataCell;

            return (
                <StaticWhitespaceCard
                    valueSales={cell.valueSales ?? ''}
                    valuePrefix={cell.valuePrefix ?? ''}
                    valueSuffix={cell.valueSuffix ?? ''}
                    trendValue={typeof cell.trendValue === 'number' ? cell.trendValue : null}
                    trendDirection={cell.trendDirection ?? 'neutral'}
                    avgValueSales={cell.avgValueSales ?? ''}
                    avgPrefix={cell.avgPrefix ?? ''}
                    shareLabel={cell.shareLabel ?? ''}
                    crossSectionShareLabel={cell.crossSectionShareLabel ?? ''}
                    crossSectionLabel={crossSection}
                    sharePercentage={cell.sharePercentage ?? 0}
                    items={paginationData.items}
                    moreCount={paginationData.moreCount}
                    heatmapColor={cell.heatmapColor ?? 'transparent'}
                    isWhitespace={showWhitespaces && Boolean(cell.isWhitespace)}
                    isMoreLoading={paginationData.isLoading}
                    showCollapseButton={paginationData.showCollapse}
                    selectedYAxisKpiId={selectedYAxisKpiId}
                    onCellHover={() => {
                        if (hoveredCellsRef.current.has(cellId)) return;
                        hoveredCellsRef.current.add(cellId);
                        trackEventMixpanel(
                            PPA_WHITESPACE_CELL_HOVER,
                            getWhitespaceCellHoverProps(gridCell, rowLabel, columnHeader, yAxis),
                        );
                    }}
                    onCollapseClick={() => {
                        collapseCellItems({ bucketLabel: rowLabel, columnHeader });
                        trackEventMixpanel(PPA_WHITESPACE_ENTITY_LIST_COLLAPSED, {
                            cell_id: cellId,
                            cross_section: crossSection,
                            total_entity_count: paginationData.items.length + paginationData.moreCount,
                        });
                    }}
                    onMoreClick={() => {
                        trackEventMixpanel(PPA_WHITESPACE_ENTITY_MORE_CLICKED, {
                            cell_id: cellId,
                            cross_section: crossSection,
                            hidden_entity_count: paginationData.moreCount,
                            visible_entity_count: paginationData.items.length,
                            sort_metric: selectedYAxisKpiId,
                        });
                        loadMoreForCell({
                            bucketLabel: rowLabel,
                            columnHeader,
                            defaultItems: cell.items ?? [],
                            defaultMoreCount: cell.moreCount ?? 0,
                        });
                    }}
                />
            );
        },
        [
            collapseCellItems,
            crossSection,
            getCellPaginationData,
            loadMoreForCell,
            selectedYAxisKpiId,
            showWhitespaces,
            yAxis,
        ],
    );

    return renderWhitespaceCell;
};

export default useWhitespaceCellRenderer;
